import { IEstablishmentFields, IEstablishmentProductFields } from '../../../../services/Event/EventService'
import {
  EstablishmentFeeType,
  EventEstablishment,
  EventEstablishmentProduct,
  EventSeriesEstablishmentTemplate
} from '../../../../services/Event/types'

/**
 * Estado dos estabelecimentos nos formulários de evento e de série (ABRA-317). As regras da taxa
 * espelham `EstablishmentFeeHelper` na API: ela sai do repasse, nunca é zero (sem taxa é a opção
 * "Sem taxa") e fica abaixo do preço — o preço inteiro é a opção "Sem repasse" (ABRA-399).
 */
export interface IEstablishmentProductForm extends IEstablishmentProductFields {
  /** Chave estável para a lista do React (não vai para a API). */
  _key: string
  /** Quanto já foi vendido (sem as canceladas) — o estoque não pode ficar abaixo disso. */
  sold_quantity: number
  /** Tem alguma venda, mesmo cancelada: a API recusa removê-lo. */
  has_orders: boolean
}

export interface IEstablishmentForm extends Omit<IEstablishmentFields, 'products'> {
  _key: string
  products: IEstablishmentProductForm[]
  /** Já vendeu: a API recusa removê-lo. */
  has_orders: boolean
}

export interface IEstablishmentTemplateForm extends IEstablishmentForm {
  /** Índices (no array de edições) em que o modelo é aplicado. */
  edition_indexes: number[]
}

export const FEE_TYPE_OPTIONS: { value: EstablishmentFeeType; label: string }[] = [
  { value: 'percent', label: 'Porcentagem' },
  { value: 'fixed', label: 'Valor fixo' },
  { value: 'none', label: 'Sem taxa' },
  { value: 'no_payout', label: 'Sem repasse (tudo para a ABRA)' }
]

/** ABRA-399 — a mesma frase da API para a taxa que leva o preço inteiro. */
export const FEE_EQUALS_PRICE =
  'Taxa igual ao preço deixa todo o valor com a ABRA: escolha "Sem repasse (tudo para a ABRA)".'

/**
 * A taxa digitada leva o preço inteiro (100%, ou fixa igual ao preço)? Isso agora é a opção "Sem
 * repasse", e a API recusa — o campo avisa antes do salvar. Em centavos, como a API compara.
 */
export const feeEqualsPrice = (product: IEstablishmentProductFields): boolean => {
  if (product.fee_value === null) return false
  if (product.fee_type === 'percent') return Math.round(product.fee_value * 100) === 10000
  if (product.fee_type === 'fixed' && product.price !== null) {
    return Math.round(product.fee_value * 100) === Math.round(product.price * 100)
  }
  return false
}

export const baseProduct = (): IEstablishmentProductForm => ({
  _key: crypto.randomUUID(),
  name: '',
  kind: 'product',
  price: null,
  stock: '',
  max_per_buyer: '',
  fee_type: '',
  fee_value: null,
  sold_quantity: 0,
  has_orders: false
})

export const baseEstablishment = (): IEstablishmentForm => ({
  _key: crypto.randomUUID(),
  name: '',
  bank_code: null,
  bank_name: null,
  bank_agency: null,
  bank_account: null,
  bank_holder_name: null,
  pix_key_type: null,
  pix_key: null,
  products: [baseProduct()],
  has_orders: false
})

const productFromApi = (product: EventEstablishmentProduct): IEstablishmentProductForm => ({
  _key: crypto.randomUUID(),
  id: product.id,
  name: product.name,
  // O `?? 'product'` cobre só a janela de deploy, com o painel novo no ar antes da API: a API nova
  // sempre manda o `kind`.
  kind: product.kind ?? 'product',
  price: Number(product.price),
  stock: product.stock ?? '',
  max_per_buyer: product.max_per_buyer ?? '',
  fee_type: product.fee_type,
  fee_value: product.fee_value === null ? null : Number(product.fee_value),
  sold_quantity: product.sold_quantity ?? 0,
  has_orders: product.has_orders ?? false
})

export const establishmentFromApi = (establishment: EventEstablishment): IEstablishmentForm => ({
  _key: crypto.randomUUID(),
  id: establishment.id,
  name: establishment.name,
  bank_code: establishment.bank_code,
  bank_name: establishment.bank_name,
  bank_agency: establishment.bank_agency,
  bank_account: establishment.bank_account,
  bank_holder_name: establishment.bank_holder_name,
  pix_key_type: establishment.pix_key_type,
  pix_key: establishment.pix_key,
  products: establishment.products.map(productFromApi),
  has_orders: establishment.has_orders ?? false
})

/** `editionIds` é a ordem das edições no formulário, para traduzir os ids da API em índices. */
export const templateFromApi = (
  template: EventSeriesEstablishmentTemplate,
  editionIds: (number | undefined)[]
): IEstablishmentTemplateForm => ({
  ...establishmentFromApi({ ...template, products: template.products, has_orders: false }),
  edition_indexes: template.edition_ids.map((id) => editionIds.indexOf(id)).filter((index) => index >= 0)
})

/** Cópia sem ids — para "Criar cópia" do evento, que nasce sem vendas. */
export const withoutIds = (establishment: IEstablishmentForm): IEstablishmentForm => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id: _id, ...rest } = establishment
  return {
    ...rest,
    _key: crypto.randomUUID(),
    has_orders: false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    products: establishment.products.map(({ id: _productId, ...product }) => ({
      ...product,
      _key: crypto.randomUUID(),
      sold_quantity: 0,
      has_orders: false
    }))
  }
}

/**
 * Taxa da ABRA por unidade, ou null enquanto preço/tipo/valor não permitem calcular. Porcentagem
 * arredondada no centavo, como a API faz por linha de venda.
 */
export const feePerUnit = (product: IEstablishmentProductFields): number | null => {
  if (product.price === null || product.fee_type === '') return null
  if (product.fee_type === 'none') return 0
  if (product.fee_type === 'no_payout') return product.price
  if (product.fee_value === null) return null
  // `toPrecision(15)` tira o erro de ponto flutuante antes de arredondar, como o `round()` do PHP:
  // R$ 0,29 a 50% é 14,4999… em JavaScript e daria R$ 0,14; a API cobra R$ 0,15.
  if (product.fee_type === 'percent') {
    return Math.round(Number((product.price * product.fee_value).toPrecision(15))) / 100
  }
  return product.fee_value
}

/**
 * O produto deixa algo para o estabelecimento? Só o gratuito e o "Sem repasse" não deixam (ABRA-399)
 * — a mesma regra de `EstablishmentFeeHelper::hasPayout` na API. A taxa que leva o preço inteiro
 * deixou de valer (é o "Sem repasse"), então o resto sempre tem repasse. Sem preço ou tipo definidos
 * ainda, também conta: os dados bancários aparecem antes de sumir, e não o contrário.
 */
export const hasPayout = (product: IEstablishmentProductFields): boolean =>
  product.price !== 0 && product.fee_type !== 'no_payout'

/** Tira os erros da API que ficaram sob `prefix`: ao remover uma linha, os índices mudam. */
export const withoutErrorsUnder = (errors: Record<string, string[]>, prefix: string): Record<string, string[]> =>
  Object.fromEntries(Object.entries(errors).filter(([key]) => !key.startsWith(prefix)))

export const anyPayout = (establishment: IEstablishmentForm): boolean => establishment.products.some(hasPayout)
